/**
 * Schedule time for tables/CSV: timezone conversion when source ≠ viewer,
 * then quarter-hour snapping (matches Add/Edit patient time pickers).
 */

export const parseTimeTo24h = (timeStr) => {
  if (!timeStr) return null;
  const t = timeStr.trim();
  const ampm = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const period = ampm[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return { h, m };
  }
  const h24 = t.match(/^(\d{1,2}):(\d{2})/);
  if (h24) return { h: parseInt(h24[1], 10), m: parseInt(h24[2], 10) };
  return null;
};

export const format24hHM = (h, m) =>
  `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

const snapToQuarterHour = (h, m) => {
  let t = h * 60 + m;
  t = Math.round(t / 15) * 15;
  t = ((t % 1440) + 1440) % 1440;
  return { h: Math.floor(t / 60), m: t % 60 };
};

const readHourMinuteInZone = (instant, timeZone) => {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(instant).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  let hh = parseInt(parts.hour, 10);
  if (hh === 24 || String(parts.hour) === "24") hh = 0;
  return { h: hh, m: parseInt(parts.minute, 10) };
};

const utcInstantFromZonedWallClock = (date_, hour, minute, timeZone) => {
  const [year, month, day] = date_.split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const readWall = (instant) => {
    const parts = formatter.formatToParts(instant).reduce((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});
    let hh = parseInt(parts.hour, 10);
    if (hh === 24 || parts.hour === "24") hh = 0;
    return {
      y: parseInt(parts.year, 10),
      mo: parseInt(parts.month, 10),
      d: parseInt(parts.day, 10),
      h: hh,
      m: parseInt(parts.minute, 10),
    };
  };

  let guessMs = Date.UTC(year, month - 1, day, hour, minute, 0);

  for (let i = 0; i < 24; i++) {
    const guess = new Date(guessMs);
    const wall = readWall(guess);
    if (
      wall.y === year &&
      wall.mo === month &&
      wall.d === day &&
      wall.h === hour &&
      wall.m === minute
    ) {
      return guess;
    }
    if (wall.y === year && wall.mo === month && wall.d === day) {
      guessMs += ((hour * 60 + minute) - (wall.h * 60 + wall.m)) * 60000;
      continue;
    }
    const tWant = Date.UTC(year, month - 1, day, 12, 0, 0);
    const tWall = Date.UTC(wall.y, wall.mo - 1, wall.d, 12, 0, 0);
    guessMs += tWant - tWall;
  }
  return null;
};

/**
 * @param {string} date_ "YYYY-MM-DD"
 * @param {string} time_ "HH:MM:SS" | "H:MM AM/PM"
 * @param {string|null} sourceTimezone row owner's IANA tz
 * @param {string|null} viewerTimezone current user's IANA tz
 * @returns {string} "HH:MM" 24h on quarter-hour grid, or "N/A"
 */
export function convertScheduledTime(
  date_,
  time_,
  sourceTimezone,
  viewerTimezone,
) {
  if (!time_) return "N/A";
  try {
    const parsed = parseTimeTo24h(time_);
    if (!parsed) {
      const raw = time_.slice(0, 5);
      const retry = parseTimeTo24h(`${raw}:00`);
      if (!retry) return raw;
      const s = snapToQuarterHour(retry.h, retry.m);
      return format24hHM(s.h, s.m);
    }

    let { h, m } = parsed;
    const canConvert =
      Boolean(date_) &&
      Boolean(sourceTimezone) &&
      Boolean(viewerTimezone) &&
      sourceTimezone !== viewerTimezone;

    if (canConvert) {
      const realUTC = utcInstantFromZonedWallClock(
        date_,
        h,
        m,
        sourceTimezone,
      );
      if (realUTC) {
        ({ h, m } = readHourMinuteInZone(realUTC, viewerTimezone));
      }
    }

    const snapped = snapToQuarterHour(h, m);
    return format24hHM(snapped.h, snapped.m);
  } catch (_) {
    const raw = time_.slice(0, 5);
    const retry = parseTimeTo24h(`${raw}:00`);
    if (!retry) return raw;
    const s = snapToQuarterHour(retry.h, retry.m);
    return format24hHM(s.h, s.m);
  }
}
